/**
 * Identity Arc
 *
 * Question: "Who is this patient?" (new patient only)
 *
 * Entry: ocrLicense (passport, ID card, or skip to manual entry)
 * Exit: done (final) — produces IdentityOutput
 *
 * Actors: analyzeOcr
 * Signals: GO_BACK (from first interactive state)
 *
 * Navigation map: see README.md
 */

import { and, assign, sendParent, setup } from "xstate";
import type { IdentityInput, IdentityOutput } from "../contracts/arc-contracts";
import type { KioskMachineContext, KioskMachineEvent } from "../types";

// Actors
import { analyzeOcrActor } from "../actors/analyze-ocr.actor";

import { isUnder18 } from "../../machine/guards/context.guard";
// Guards
import { isQrMode } from "../guards/context.guard";
import {
  selectedIdCard,
  selectedPassport,
} from "../guards/id-type-selection.guard";

import { clearOcrDerivedData } from "../actions/clear-ocr-derived-data.action";
import { saveActorResult } from "../actions/save-actor-result.action";
import { saveError } from "../actions/save-error.action";
// Actions
import { saveFormData } from "../actions/save-form-data.action";
import { saveOCRresponsesToIntakeAddress } from "../actions/save-ocr-responses-to-intake-address.action";
import { savePhotoData } from "../actions/save-photo-data.action";

// Helpers
import { parseOcrDate } from "../../lib/ocr/date-parser";
import { fromISODate, hasCompletedBefore } from "../helper/arc-helpers";
import { peekScreen, popScreen, pushScreen } from "../helper/screen-history";

/**
 * Seed arc context from input.
 * Uses KioskMachineContext (wide type) initially — see AGENTS.md Typing Strategy.
 */
function createIdentityContext(input: IdentityInput): KioskMachineContext {
  // If re-invoked, restore from previous output and flag for routing
  if (input.previousOutput) {
    return {
      orgId: input.orgId,
      orgName: input.orgName,
      practiceId: input.practiceId,
      language: input.language,
      ...input.previousOutput,
      // Intake data lives on the top-level input, not in previousOutput
      intakeFormSections: input.intakeFormSections,
      responses: input.responses,
      formIds: input.formIds,
      _reInvoked: true,
      _reviewMode: input.reviewMode,
      _verifyMode: input.verifyMode,
      _screenHistory: input.previousOutput._screenHistory ?? [],
      // Email match flow
      otpDeclined: input.otpDeclined,
      verified: input.verified,
      phoneNumber: input.phoneNumber,
      _resumeAtEmailInput: input.resumeAtEmailInput,
      qrMode: input.qrMode,
    };
  }

  return {
    orgId: input.orgId,
    orgName: input.orgName,
    practiceId: input.practiceId,
    language: input.language,
    under18: input.under18,
    _reviewMode: input.reviewMode,
    _verifyMode: input.verifyMode,
    _screenHistory: [],
    // Pre-populate from returning patient lookup (if available)
    firstName: input.firstName,
    lastName: input.lastName,
    sex: input.sex,
    email: input.email,
    dateOfBirth: input.dateOfBirth,
    // Intake data (passed through to intake arc)
    intakeFormSections: input.intakeFormSections,
    responses: input.responses,
    formIds: input.formIds,
    // Email match flow
    otpDeclined: input.otpDeclined,
    verified: input.verified,
    phoneNumber: input.phoneNumber,
    _resumeAtEmailInput: input.resumeAtEmailInput,
    qrMode: input.qrMode,
  };
}

/**
 * Extract typed output from arc context at final state.
 */
function extractIdentityOutput(context: KioskMachineContext): IdentityOutput {
  return {
    firstName: (context.firstName as string) ?? "",
    lastName: (context.lastName as string) ?? "",
    dateOfBirth: (context.dateOfBirth as string) ?? "",
    sex: (context.sex as string) ?? "",
    email: context.email as string | undefined,
    ocrData: context.ocrData,
    licensePhotoFront: context.licensePhotoFront,
    licensePhotoBack: context.licensePhotoBack,
    hasPhysicalId: context.hasPhysicalId,
    passportPhoto: context.passportPhoto,
    idType: context.idType,
    // Only include insurances if set — avoid overwriting parent's insurance data
    ...(context.insurances ? { insurances: context.insurances } : {}),
    _screenHistory: context._screenHistory as string[] | undefined,
    // Pass-through for intake arc
    responses: context.responses as Record<string, unknown> | undefined,
    formIds: context.formIds as number[] | undefined,
    intakeFormSections: context.intakeFormSections as
      | IdentityOutput["intakeFormSections"]
      | undefined,
  };
}

// ─── Machine Definition ──────────────────────────────────────

export const identityArc = setup({
  types: {
    context: {} as KioskMachineContext,
    events: {} as KioskMachineEvent,
    input: {} as IdentityInput,
    output: {} as IdentityOutput,
  },
  actors: {
    analyzeOcr: analyzeOcrActor.impl,
  },
  guards: {
    selectedPassport: selectedPassport.impl,
    selectedIdCard: selectedIdCard.impl,
    hasCompletedBefore,
    isQrMode: isQrMode.impl,
    isReviewMode: ({ context }) => !!context._reviewMode,
    isVerifyMode: ({ context }) => !!context._verifyMode,
    historyEmpty: ({ context }) =>
      ((context._screenHistory as string[]) ?? []).length === 0,
    missingName: ({ context }) => !context.firstName || !context.lastName,
    missingDOB: ({ context }) => !context.dateOfBirth,
    missingAddress: ({ context }) => {
      const responses = context.responses as
        | Record<string, unknown>
        | undefined;
      return !responses || Object.keys(responses).length === 0;
    },
    missingEmail: ({ context }) => !context.email,
    missingSex: ({ context }) => !context.sex,
    skipDob: ({ context }) => {
      // No OCR: skip only if returning patient already has a DOB
      if (!context.ocrData)
        return !!context._skippedIdCapture && !!context.dateOfBirth;
      // Review mode: never auto-skip DOB
      if (context._reviewMode) return false;
      // OCR ran but no DOB extracted: show DOB screen
      if (!context.ocrData.dob) return false;
      // No prior DOB to compare against: show DOB screen
      if (!context._preOcrDateOfBirth || !context.dateOfBirth) return false;
      // Skip if OCR DOB matches what we already had
      return (
        fromISODate(context.dateOfBirth) ===
        fromISODate(context._preOcrDateOfBirth)
      );
    },
    skipEmailMatch: ({ context }) =>
      !!context.otpDeclined || !!context.verified,
    resumeAtEmailInput: ({ context }) => !!context._resumeAtEmailInput,
    isUnder18: isUnder18.impl,
  },
  actions: {
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    saveFormData: saveFormData.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    saveError: saveError.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    saveActorResult: saveActorResult.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    savePhotoData: savePhotoData.impl as any,
    saveOCRresponsesToIntakeAddress:
      // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
      saveOCRresponsesToIntakeAddress.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    clearOcrDerivedData: clearOcrDerivedData.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState cross-arc sendParent event typing
    signalMatchByEmail: sendParent(({ context, event }: any) => ({
      type: "MATCH_BY_EMAIL" as const,
      // biome-ignore lint/suspicious/noExplicitAny: event.data shape varies by form submission
      email: (event as any).data?.email ?? context.email,
      firstName: context.firstName,
      lastName: context.lastName,
      dateOfBirth: context.dateOfBirth,
      phoneNumber: context.phoneNumber,
      responses: context.responses,
      // Preserve photo data captured before email match destroys the arc
      licensePhotoFront: context.licensePhotoFront,
      licensePhotoBack: context.licensePhotoBack,
      passportPhoto: context.passportPhoto,
      hasPhysicalId: context.hasPhysicalId,
      idType: context.idType,
      ocrData: context.ocrData,
    })),
  },
}).createMachine({
  id: "identity",
  context: ({ input }) => createIdentityContext(input),
  output: ({ context }) => extractIdentityOutput(context),
  initial: "route",

  states: {
    // ===== ENTRY ROUTING =====
    route: {
      always: [
        // Email match BACK: resume at emailInput
        { guard: "resumeAtEmailInput", target: "emailInput" },
        // Verify mode re-invocation (e.g. BACK from intake pharmacy):
        // walk backward through the verify screens the user already
        // visited instead of re-running verifyRoute (which would
        // auto-complete to `done` because nothing is missing anymore,
        // causing an infinite loop back into intake).
        {
          guard: and(["isVerifyMode", "hasCompletedBefore"]),
          target: "verifyBack",
        },
        // Verify mode first pass: only show screens with missing data
        { guard: "isVerifyMode", target: "verifyRoute" },
        // Re-invocation: jump to exit-adjacent state with data pre-filled
        { guard: "hasCompletedBefore", target: "sexInput" },
        // Review mode: start at name confirmation (full review flow)
        { guard: "isReviewMode", target: "fullNameInput" },
        // First invocation: start from the beginning (new patient OCR/manual entry)
        { target: "ocrLicense" },
      ],
    },

    // ===== VERIFY MODE ROUTING =====
    verifyRoute: {
      always: [
        { guard: "missingName", target: "fullNameInput" },
        { guard: "missingDOB", target: "dobInput" },
        { guard: "missingAddress", target: "addressInput" },
        { guard: "missingEmail", target: "emailInput" },
        { guard: "missingSex", target: "sexInput" },
        // All fields present — shouldn't happen but fallback
        { target: "done" },
      ],
    },

    verifyNextRoute: {
      always: [
        { guard: "missingDOB", target: "dobInput" },
        { guard: "missingAddress", target: "addressInput" },
        { guard: "missingEmail", target: "emailInput" },
        { guard: "missingSex", target: "sexInput" },
        // All remaining fields filled
        { target: "done" },
      ],
    },

    // ===== VERIFY-MODE BACKWARD WALK =====
    //
    // Handles two entry cases:
    //
    //   1. Re-invocation entry (from the `route` state): the user has
    //      filled missing verify fields on a prior pass, left the arc,
    //      and is now returning via BACK. `_screenHistory` contains the
    //      forward-pushed verify screens in visit order. We pop the top
    //      (the "last visited" screen) and target it — the user lands
    //      on that screen with `_screenHistory.top` now pointing at its
    //      predecessor, satisfying the standard BACK-chain invariant.
    //
    //   2. Per-screen BACK (from any verify screen's BACK branch when
    //      `isVerifyMode`): pop the current screen off history and
    //      target the new top. When history is empty, sendParent
    //      GO_BACK so the parent routes back to returningPatient.
    //
    // Both cases are served by the same "peek → target popped value"
    // transitions below. The pop happens atomically on the transition
    // (as an action), so the resulting history top is the new predecessor.
    verifyBack: {
      always: [
        // Empty history → exit arc via parent signal
        {
          guard: "historyEmpty",
          actions: sendParent({ type: "GO_BACK" }),
        },
        // Peek the top of history; pop + target that screen
        {
          guard: ({ context }) => peekScreen(context) === "fullNameInput",
          target: "fullNameInput",
          actions: [popScreen()],
        },
        {
          guard: ({ context }) => peekScreen(context) === "dobInput",
          target: "dobInput",
          actions: [popScreen()],
        },
        {
          guard: ({ context }) => peekScreen(context) === "addressInput",
          target: "addressInput",
          actions: [popScreen()],
        },
        {
          guard: ({ context }) => peekScreen(context) === "emailInput",
          target: "emailInput",
          actions: [popScreen()],
        },
        {
          guard: ({ context }) => peekScreen(context) === "sexInput",
          target: "sexInput",
          actions: [popScreen()],
        },
        // Unknown screen name in history → defensively exit the arc
        { actions: sendParent({ type: "GO_BACK" }) },
      ],
    },

    // ===== OCR LICENSE / PASSPORT FLOW =====
    ocrLicense: {
      initial: "idTypeSelection",
      states: {
        idTypeSelection: {
          meta: {
            componentName: "IdTypeSelection",
            contentKey: "medrite.checkin.id-type-selection",
          },
          on: {
            SUBMIT: [
              {
                // QR mode + passport → upload flow (phone photo upload)
                guard: ({ context, event }) =>
                  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
                  isQrMode.impl({ context, event } as any) &&
                  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
                  selectedPassport.impl({ context, event } as any),
                target: "uploadPassportPhoto",
                actions: [
                  "saveFormData",
                  assign({ _skippedIdCapture: false }),
                  pushScreen("ocrLicense.idTypeSelection"),
                ],
              },
              {
                guard: "selectedPassport",
                target: "passportPhoto",
                actions: [
                  "saveFormData",
                  assign({ _skippedIdCapture: false }),
                  pushScreen("ocrLicense.idTypeSelection"),
                ],
              },
              {
                // QR mode + ID card → upload flow (phone photo upload)
                guard: ({ context, event }) =>
                  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
                  isQrMode.impl({ context, event } as any) &&
                  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
                  selectedIdCard.impl({ context, event } as any),
                target: "uploadLicenseFrontPhoto",
                actions: [
                  "saveFormData",
                  assign({ _skippedIdCapture: false }),
                  pushScreen("ocrLicense.idTypeSelection"),
                ],
              },
              {
                guard: "selectedIdCard",
                target: "licenseFrontPhoto",
                actions: [
                  "saveFormData",
                  assign({ _skippedIdCapture: false }),
                  pushScreen("ocrLicense.idTypeSelection"),
                ],
              },
              {
                target: "#identity.fullNameInput",
                actions: [
                  "saveFormData",
                  "clearOcrDerivedData",
                  assign({ _skippedIdCapture: true }),
                  pushScreen("ocrLicense.idTypeSelection"),
                ],
              },
            ],
            BACK: {
              // Arc boundary: signal parent to go back (to returningPatient arc)
              actions: sendParent({ type: "GO_BACK" }),
            },
          },
        },

        // ── Passport flow ──────────────────────────────────────

        passportPhoto: {
          meta: {
            componentName: "PhotoCapture",
            contentKey: "medrite.checkin.photo-capture",
            metadata: {
              cardType: "passport",
              side: "front",
              autoSubmit: true,
            },
          },
          on: {
            CONFIRM: [
              {
                guard: "isUnder18",
                target: "#identity.fullNameInput",
                actions: ["savePhotoData"],
              },
              {
                target: "processingOcrPassport",
                actions: ["savePhotoData"],
              },
            ],
            SKIP: {
              target: "#identity.fullNameInput",
              actions: [
                "clearOcrDerivedData",
                assign({ _skippedIdCapture: true }),
              ],
            },
            BACK: { target: "idTypeSelection" },
          },
        },

        processingOcrPassport: {
          meta: {
            componentName: "LoadingSpinner",
            contentKey: "medrite.checkin.processing-id",
          },
          invoke: {
            id: "analyzeOcrPassport",
            src: "analyzeOcr",
            input: ({ context }) => ({
              mode: "id" as const,
              imageBase64: context.passportPhoto,
              contextKey: "ocrData" as const,
              consumerId: context.consumerId,
              orgId: context.orgId,
              confidenceThreshold: 0.85,
            }),
            onDone: {
              target: "saveOcrToContext",
              actions: ["saveActorResult"],
            },
            onError: {
              target: "#identity.fullNameInput",
              actions: ["saveError"],
            },
          },
        },

        // ── ID card (license) flow ─────────────────────────────

        licenseFrontPhoto: {
          meta: {
            componentName: "PhotoCapture",
            contentKey: "medrite.checkin.photo-capture",
            metadata: {
              cardType: "license",
              side: "front",
              autoSubmit: true,
            },
          },
          on: {
            CONFIRM: {
              target: "licenseBackPhoto",
              actions: ["savePhotoData"],
            },
            SKIP: {
              target: "#identity.fullNameInput",
              actions: [
                "clearOcrDerivedData",
                assign({ _skippedIdCapture: true }),
              ],
            },
            BACK: { target: "idTypeSelection" },
          },
        },

        licenseBackPhoto: {
          meta: {
            componentName: "PhotoCapture",
            contentKey: "medrite.checkin.photo-capture",
            metadata: {
              cardType: "license",
              side: "back",
              autoSubmit: true,
            },
          },
          on: {
            CONFIRM: [
              {
                guard: "isUnder18",
                target: "#identity.fullNameInput",
                actions: ["savePhotoData"],
              },
              {
                target: "processingOcrId",
                actions: ["savePhotoData"],
              },
            ],
            SKIP: {
              target: "#identity.fullNameInput",
              actions: [
                "clearOcrDerivedData",
                assign({ _skippedIdCapture: true }),
              ],
            },
            BACK: { target: "licenseFrontPhoto" },
          },
        },

        // ── QR mode upload flow (phone) ────────────────────────

        uploadPassportPhoto: {
          meta: {
            componentName: "PhotoUpload",
            contentKey: "medrite.checkin.upload-license-photo",
            metadata: {
              cardType: "passport",
              side: "front",
            },
          },
          on: {
            CONFIRM: {
              target: "processingOcrPassport",
              actions: ["savePhotoData"],
            },
            TAKE_PHOTO: {
              target: "passportPhoto",
            },
            SKIP: {
              target: "#identity.fullNameInput",
              actions: [
                "clearOcrDerivedData",
                assign({ _skippedIdCapture: true }),
              ],
            },
            BACK: { target: "idTypeSelection" },
          },
        },

        uploadLicenseFrontPhoto: {
          meta: {
            componentName: "PhotoUpload",
            contentKey: "medrite.checkin.upload-license-photo",
            metadata: {
              cardType: "license",
              side: "front",
            },
          },
          on: {
            CONFIRM: {
              target: "uploadLicenseBackPhoto",
              actions: ["savePhotoData"],
            },
            TAKE_PHOTO: {
              target: "licenseFrontPhoto",
            },
            SKIP: {
              target: "#identity.fullNameInput",
              actions: [
                "clearOcrDerivedData",
                assign({ _skippedIdCapture: true }),
              ],
            },
            BACK: { target: "idTypeSelection" },
          },
        },

        uploadLicenseBackPhoto: {
          meta: {
            componentName: "PhotoUpload",
            contentKey: "medrite.checkin.upload-license-photo",
            metadata: {
              cardType: "license",
              side: "back",
            },
          },
          on: {
            CONFIRM: {
              target: "processingOcrId",
              actions: ["savePhotoData"],
            },
            TAKE_PHOTO: {
              target: "licenseBackPhoto",
            },
            SKIP: {
              target: "processingOcrId",
            },
            BACK: { target: "uploadLicenseFrontPhoto" },
          },
        },

        processingOcrId: {
          meta: {
            componentName: "LoadingSpinner",
            contentKey: "medrite.checkin.processing-id",
          },
          invoke: {
            id: "analyzeOcr",
            src: "analyzeOcr",
            input: ({ context }) => ({
              mode: "id" as const,
              imageBase64: context.licensePhotoFront,
              contextKey: "ocrData" as const,
              consumerId: context.consumerId,
              orgId: context.orgId,
              confidenceThreshold: 0.85,
            }),
            onDone: {
              target: "saveOcrToContext",
              actions: ["saveActorResult"],
            },
            onError: {
              target: "#identity.fullNameInput",
              actions: ["saveError"],
            },
          },
        },

        // ── Shared OCR post-processing ─────────────────────────

        saveOcrToContext: {
          // XState assign reads all context values before applying any updates,
          // so _preOcrDateOfBirth captures the pre-OCR value even though
          // dateOfBirth is overwritten in the same assign block.
          entry: assign({
            _preOcrDateOfBirth: ({ context }) => context.dateOfBirth,
            firstName: ({ context }) => {
              return context.ocrData?.firstName ?? context.firstName;
            },
            lastName: ({ context }) => {
              return context.ocrData?.lastName ?? context.lastName;
            },
            dateOfBirth: ({ context }) => {
              const dob = context.ocrData?.dob;
              return dob ? parseOcrDate(dob) : context.dateOfBirth;
            },
            sex: ({ context }) => {
              const sex = context.ocrData?.sex;
              return sex === "M"
                ? "Male"
                : sex === "F"
                  ? "Female"
                  : context.sex;
            },
          }),
          always: {
            target: "#identity.fullNameInput",
            actions: ["saveOCRresponsesToIntakeAddress"],
          },
        },
      },
    },

    // ===== NAME / DOB / ADDRESS ENTRY =====
    fullNameInput: {
      meta: {
        componentName: "FullNameInput",
        contentKey: "medrite.checkin.full-name-input",
      },
      on: {
        SUBMIT: [
          {
            guard: "isVerifyMode",
            target: "verifyNextRoute",
            actions: ["saveFormData", pushScreen("fullNameInput")],
          },
          {
            target: "dobInput",
            actions: ["saveFormData", pushScreen("fullNameInput")],
          },
        ],
        BACK: [
          { guard: "isReviewMode", actions: sendParent({ type: "GO_BACK" }) },
          // Verify mode: walk backward through _screenHistory
          { guard: "isVerifyMode", target: "verifyBack" },
          { target: "ocrLicense", actions: [popScreen()] },
        ],
      },
    },

    dobInput: {
      always: [{ guard: "skipDob", target: "addressInput" }],
      meta: {
        componentName: "DOBInput",
        contentKey: "medrite.checkin.dob-input",
      },
      on: {
        SUBMIT: [
          {
            guard: "isVerifyMode",
            target: "verifyNextRoute",
            actions: ["saveFormData", pushScreen("dobInput")],
          },
          {
            target: "addressInput",
            actions: ["saveFormData", pushScreen("dobInput")],
          },
        ],
        BACK: [
          // Verify mode: walk backward through _screenHistory
          { guard: "isVerifyMode", target: "verifyBack" },
          { target: "fullNameInput", actions: [popScreen()] },
        ],
      },
    },

    addressInput: {
      meta: {
        componentName: "IntakeQuestions",
        contentKey: "medrite.checkin.intake-address",
        metadata: {
          sectionName: "Address",
        },
      },
      on: {
        SUBMIT: [
          {
            guard: "isVerifyMode",
            target: "verifyNextRoute",
            actions: ["saveFormData", pushScreen("addressInput")],
          },
          {
            target: "emailInput",
            actions: ["saveFormData", pushScreen("addressInput")],
          },
        ],
        BACK: [
          // Verify mode: walk backward through _screenHistory
          { guard: "isVerifyMode", target: "verifyBack" },
          // Skip back over dobInput if it was never visited (auto-skipped on forward pass)
          {
            guard: ({ context }) =>
              !((context._screenHistory as string[]) ?? []).includes(
                "dobInput",
              ),
            target: "fullNameInput",
            actions: [popScreen()],
          },
          { target: "dobInput", actions: [popScreen()] },
        ],
      },
    },

    sexInput: {
      meta: {
        componentName: "SexInput",
        contentKey: "medrite.checkin.sex-input",
        metadata: {
          sectionName: "Sex",
        },
      },
      on: {
        // NOTE: SUBMIT intentionally does NOT call `pushScreen("sexInput")`.
        // The BACK-chain invariant is "history top = the previous screen,
        // not the current one". sexInput is the terminal interactive
        // screen (SUBMIT → done), so pushing it would leave the current
        // screen on top of history on re-invocation.
        SUBMIT: {
          target: "done",
          actions: ["saveFormData"],
        },
        BACK: [
          // Verify mode: walk backward through _screenHistory
          { guard: "isVerifyMode", target: "verifyBack" },
          { target: "emailInput", actions: [popScreen()] },
        ],
      },
    },

    emailInput: {
      meta: {
        componentName: "ContactInput",
        contentKey: "medrite.checkin.contact-input-email",
      },
      on: {
        SUBMIT: [
          {
            guard: "isVerifyMode",
            target: "verifyNextRoute",
            actions: ["saveFormData", pushScreen("emailInput")],
          },
          // OTP was declined or patient already verified: skip match, go directly to sex input
          {
            guard: "skipEmailMatch",
            target: "sexInput",
            actions: ["saveFormData", pushScreen("emailInput")],
          },
          // Otherwise: save email, signal parent to do email match, show spinner
          {
            target: "awaitingEmailMatch",
            actions: ["saveFormData", "signalMatchByEmail"],
          },
        ],
        BACK: [
          // Verify mode: walk backward through _screenHistory
          { guard: "isVerifyMode", target: "verifyBack" },
          { target: "addressInput", actions: [popScreen()] },
        ],
      },
    },

    awaitingEmailMatch: {
      meta: {
        componentName: "LoadingSpinner",
        contentKey: "medrite.checkin.processing-id",
      },
      // No transitions — parent destroys this arc when it receives MATCH_BY_EMAIL
    },

    // ===== EXIT =====
    done: {
      type: "final" as const,
    },
  },
});
